/**
 * Message Service - Handle sending and receiving messages
 */

import { EventEmitter } from '../core/events.js';

export class MessageService extends EventEmitter {
  constructor(client) {
    super();
    this.client = client;
    this.conversations = new Map();
    this.messageHistory = [];
  }

  /**
   * Send a message
   */
  async send(recipientId, content, options = {}) {
    const messageId = this._generateId();
    const timestamp = Date.now();

    const message = {
      id: messageId,
      type: options.type || 'text',
      sender_id: this.client.state.userId,
      recipient_id: recipientId,
      content: this._encodeContent(content),
      timestamp: timestamp,
      thread_id: options.threadId || null,
      reply_to: options.replyTo || null,
    };

    // Sign message if we have keys
    if (this.client.identity.keyPair) {
      message.signature = await this._signMessage(message);
    }

    // Store locally first
    await this._storeMessage(message);

    // Send via WebSocket if connected
    if (this.client.isConnected()) {
      this.client.network.send({
        type: 'message',
        payload: message,
      });
    } else {
      // Queue for later
      this.client._messageQueue.push(message);
    }

    this.emit('messageSent', message);

    return {
      success: true,
      messageId: messageId,
      timestamp: timestamp,
    };
  }

  /**
   * Send via HTTP API (fallback)
   */
  async sendHttp(recipientId, content, options = {}) {
    const message = {
      id: this._generateId(),
      recipient_id: recipientId,
      content: this._encodeContent(content),
      type: options.type || 'text',
      signature: options.signature || '',
    };

    const result = await this.client.network.apiPost('/api/v1/messages/send', message);

    if (result.success) {
      this.emit('messageSent', message);
    }

    return result;
  }

  /**
   * Get message history
   */
  async getHistory(options = {}) {
    const query = new URLSearchParams();
    if (options.limit) query.append('limit', options.limit);
    if (options.withUser) query.append('with_user', options.withUser);
    if (options.after) query.append('after', options.after);

    const result = await this.client.network.apiGet(`/api/v1/messages/history?${query}`);

    if (result.success && result.messages) {
      // Decode messages
      const messages = result.messages.map(msg => ({
        ...msg,
        content: this._decodeContent(msg.content),
      }));

      this.emit('historyLoaded', messages);
      return messages;
    }

    return [];
  }

  /**
   * Mark message as read
   */
  async markAsRead(messageIds) {
    if (!Array.isArray(messageIds)) {
      messageIds = [messageIds];
    }

    this.client.network.send({
      type: 'read_receipt',
      payload: {
        message_ids: messageIds,
        timestamp: Date.now(),
      },
    });
  }

  /**
   * Handle incoming message
   */
  _handleIncomingMessage(msg) {
    const message = {
      ...msg.payload,
      content: this._decodeContent(msg.payload.content),
      receivedAt: Date.now(),
    };

    // Store message
    this._storeMessage(message);

    // Update conversation
    this._updateConversation(message);

    this.emit('messageReceived', message);
  }

  /**
   * Handle delivery receipt
   */
  _handleDeliveryReceipt(msg) {
    this.emit('deliveryReceipt', msg.payload);
  }

  /**
   * Get conversation list
   */
  getConversations() {
    return Array.from(this.conversations.values())
      .sort((a, b) => b.lastMessageAt - a.lastMessageAt);
  }

  /**
   * Get messages for a conversation
   */
  getConversationMessages(userId, limit = 50) {
    return this.messageHistory
      .filter(msg => msg.sender_id === userId || msg.recipient_id === userId)
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(-limit);
  }

  _encodeContent(content) {
    if (typeof content === 'string') {
      return btoa(unescape(encodeURIComponent(content)));
    }
    return btoa(JSON.stringify(content));
  }

  _decodeContent(content) {
    try {
      const decoded = atob(content);
      try {
        return JSON.parse(decoded);
      } catch {
        return decodeURIComponent(escape(decoded));
      }
    } catch {
      return content;
    }
  }

  async _signMessage(message) {
    // TODO: Implement Ed25519 signing
    return '';
  }

  async _storeMessage(message) {
    this.messageHistory.push(message);

    // Persist to storage
    await this.client.storage.set(`msg_${message.id}`, message);
  }

  _updateConversation(message) {
    const otherParty = message.sender_id === this.client.state.userId
      ? message.recipient_id
      : message.sender_id;

    const existing = this.conversations.get(otherParty);
    this.conversations.set(otherParty, {
      userId: otherParty,
      lastMessage: message.content,
      lastMessageAt: message.timestamp,
      unreadCount: existing ? existing.unreadCount + 1 : 1,
    });

    this.emit('conversationUpdated', this.conversations.get(otherParty));
  }

  _generateId() {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
